import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminChangePassword } from '../config/api';
import RecoveryCodeModal from './RecoveryCodeModal';

const inputStyle = {
  width: '100%',
  border: '1px solid var(--color-border)',
  background: 'rgba(255, 255, 255, 0.05)',
  padding: '12px 14px',
  fontSize: 15,
  color: '#fff',
  outline: 'none',
  fontFamily: 'var(--font-body)',
  borderRadius: 4,
  transition: 'all 0.2s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#999',
  marginBottom: 8,
  fontFamily: 'var(--font-body)',
  fontWeight: 500,
};

function PasswordField({ label, value, onChange, placeholder, show, onToggle }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          style={{ ...inputStyle, paddingRight: '60px' }}
          placeholder={placeholder}
          autoComplete="new-password"
          onFocus={(e) => (e.target.style.borderColor = 'var(--color-accent)')}
          onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
        />
        <button
          type="button"
          onClick={onToggle}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            fontSize: 11,
            color: '#999',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            padding: '4px 8px',
            borderRadius: 3,
          }}
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await adminChangePassword(currentPassword, newPassword);
      setRecoveryCode(res.data.data.recovery_code);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Current password is incorrect');
      } else {
        setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (recoveryCode) {
    return (
      <RecoveryCodeModal
        recoveryCode={recoveryCode}
        onConfirm={() => navigate('/admin/dashboard')}
      />
    );
  }

  return (
    <>
      <style>{`input::placeholder { color: #666; opacity: 1; }`}</style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--color-dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          background: 'var(--color-surface)',
          padding: '40px 36px',
          width: '100%',
          maxWidth: 360,
          borderRadius: 4,
        }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 28,
            fontWeight: 400,
            textAlign: 'center',
            color: 'var(--color-text)',
            marginBottom: 6,
          }}>
            Change Password
          </h1>

          <p style={{
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--color-muted)',
            fontFamily: 'var(--font-body)',
            marginBottom: 28,
          }}>
            Set a new password for your account
          </p>

          {error && (
            <div style={{
              background: '#fff5f5',
              borderLeft: '3px solid var(--color-error)',
              padding: '10px 12px',
              fontSize: 12,
              color: 'var(--color-error)',
              marginBottom: 18,
              fontFamily: 'var(--font-body)',
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <input type="password" style={{ display: 'none' }} readOnly />

            <PasswordField
              label="Current Password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setError(''); }}
              placeholder="Enter current password"
              show={showCurrent}
              onToggle={() => setShowCurrent((v) => !v)}
            />

            <PasswordField
              label="New Password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              placeholder="Enter new password"
              show={showNew}
              onToggle={() => setShowNew((v) => !v)}
            />

            <PasswordField
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="Confirm new password"
              show={showConfirm}
              onToggle={() => setShowConfirm((v) => !v)}
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: loading ? '#777' : 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                fontSize: 13,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                borderRadius: 4,
                fontWeight: 600,
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Saving...' : 'Save Password'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
